// Pure assembly of chart series from API rows.
//
// Honesty rules enforced here:
// - The x axis is a continuous epoch spine trimmed to the first and last
//   epoch with at least one plottable (post-gate) value; interior all-null
//   epochs stay as visible gaps. Nothing is interpolated, nothing coerced
//   to 0.
// - Network distribution columns are coverage-gated: a median or band
//   measured from under half the cluster's validators is not presented as a
//   network statistic (same principle as the API's cluster_mev_apr_pct
//   gate). Gated values plot as null; the raw cluster row stays in byEpoch
//   so tooltips disclose the coverage instead of hiding it.

import type { ApyRow, ClusterData, ClusterRow, IncomeRow, RankRow } from "./api";

/** Minimum distribution_validators / validators ratio to plot the network series. */
export const DISTRIBUTION_COVERAGE_MIN = 0.5;

export interface EpochJoined {
  apy?: ApyRow;
  cluster?: ClusterRow;
  income?: IncomeRow;
  rank?: RankRow;
}

export interface DistributionCoverage {
  /** Validators that entered the distribution that epoch. */
  measured: number;
  /** Cluster validator count; null when the row does not carry it. */
  total: number | null;
  /** measured / total >= DISTRIBUTION_COVERAGE_MIN; false when total is unknown. */
  sufficient: boolean;
}

function coverageOf(measured: number | null, total: number | null): DistributionCoverage | null {
  if (measured === null) {
    return null;
  }
  const sufficient =
    total !== null && total > 0 && measured / total >= DISTRIBUTION_COVERAGE_MIN;
  return { measured, total, sufficient };
}

/**
 * Coverage of the NET (incl. MEV) distribution for one cluster row.
 *
 * Returns null when distribution_validators is null (no distribution at
 * all). An unknown validator total can never be sufficient: coverage that
 * cannot be verified is not presented as a network statistic.
 */
export function distributionCoverage(c: ClusterRow | undefined): DistributionCoverage | null {
  return coverageOf(c?.distribution_validators ?? null, c?.validators ?? null);
}

/**
 * Coverage of the BASE (inflation-only) distribution for one cluster row.
 *
 * Same gate as distributionCoverage, read from distribution_infl_validators
 * instead. The base distribution needs no stake capture, so it can pass
 * this gate on epochs the net distribution above does not.
 */
export function distributionCoverageInfl(c: ClusterRow | undefined): DistributionCoverage | null {
  return coverageOf(c?.distribution_infl_validators ?? null, c?.validators ?? null);
}

export interface Assembled {
  /** Continuous ascending epochs, trimmed to the plottable range. */
  epochs: number[];
  /** This validator's delegator_apy_pct per epoch (null = gap). */
  our: (number | null)[];
  /** Network median (delegator_apy_p50_pct), coverage-gated. */
  median: (number | null)[];
  /** Band lower edge: delegator_apy_p90_pct, coverage-gated. */
  p90: (number | null)[];
  /** Band upper edge: delegator_apy_max_pct, coverage-gated. */
  max: (number | null)[];
  /** max - p90, for the stacked band fill; null unless BOTH edges plot. */
  bandDelta: (number | null)[];
  /** distribution_validators per epoch, null where the gate suppressed it. */
  distributionValidators: (number | null)[];
  /**
   * True where a /cluster row exists with a distribution but coverage fell
   * under DISTRIBUTION_COVERAGE_MIN — the epoch whose median/band the gate
   * withheld. False (not just null-coverage) when there was no distribution
   * at all; that is plain absence, not a gated measurement.
   */
  distributionGated: boolean[];
  /**
   * True where a /cluster row exists but carries no distribution at all yet.
   *
   * Distinct from distributionGated: that is a measurement withheld for thin
   * coverage, this is a measurement not made. The daemon computes an epoch's
   * distribution during the FOLLOWING epoch, so the newest epoch is normally
   * pending — our own line legitimately runs past the cluster's, permanently.
   * False on API versions that serve no distribution keys (plain absence of
   * the feature, not of the measurement).
   */
  distributionPending: boolean[];
  /** Any non-null plotted median point exists. */
  hasMedian: boolean;
  /** Any epoch has both plotted band edges. */
  hasBand: boolean;
  /** False on older APIs that serve /cluster rows without distribution keys. */
  distributionKeysPresent: boolean;

  // --- Base (inflation-only) mirror of the columns above. Populated
  // regardless of inflationKeysPresent (null throughout when the API does
  // not serve the columns yet); mode selection is the caller's job.
  /** This validator's delegator_apy_inflation_pct per epoch (null = gap). */
  ourBase: (number | null)[];
  /** Network base median (delegator_apy_infl_p50_pct), coverage-gated. */
  medianBase: (number | null)[];
  /** Base band lower edge: delegator_apy_infl_p90_pct, coverage-gated. */
  p90Base: (number | null)[];
  /** Base band upper edge: delegator_apy_infl_max_pct, coverage-gated. */
  maxBase: (number | null)[];
  /** maxBase - p90Base, for the stacked band fill; null unless BOTH edges plot. */
  bandDeltaBase: (number | null)[];
  /** distribution_infl_validators per epoch, null where the gate suppressed it. */
  distributionInflValidators: (number | null)[];
  /** Same semantics as distributionGated, for the base distribution. */
  distributionInflGated: boolean[];
  /** Same semantics as distributionPending, for the base distribution. */
  distributionInflPending: boolean[];
  /** Any non-null plotted base median point exists. */
  hasMedianBase: boolean;
  /** Any epoch has both plotted base band edges. */
  hasBandBase: boolean;
  /** False on API versions that serve /cluster rows without base distribution keys (pre-0.7.0). */
  inflationKeysPresent: boolean;

  /**
   * This validator's percentile inside the epoch's measured BASE
   * distribution (0-100), passed through raw from the /cluster row — null
   * wherever the API does not yet serve `our_base_percentile_pct`. Not
   * coverage-gated: the API decides whether to serve a value.
   */
  ourBasePercentile: (number | null)[];
  /** Matching 1-based rank for ourBasePercentile; null wherever that is null. */
  ourBaseRank: (number | null)[];

  /** Join of all three endpoints by epoch (raw, ungated), for tooltips and drill-down. */
  byEpoch: Map<number, EpochJoined>;
  /** First and last income rows inside the trimmed range, for the subtitle. */
  incomeFirst: IncomeRow | null;
  incomeLast: IncomeRow | null;
}

function inRange(epoch: number, fromEpoch?: number, toEpoch?: number): boolean {
  if (fromEpoch !== undefined && epoch < fromEpoch) {
    return false;
  }
  if (toEpoch !== undefined && epoch > toEpoch) {
    return false;
  }
  return true;
}

interface PlotRow {
  epoch: number;
  our: number | null;
  median: number | null;
  p90: number | null;
  max: number | null;
  dist: number | null;
  gated: boolean;
  pending: boolean;
  ourBase: number | null;
  medianBase: number | null;
  p90Base: number | null;
  maxBase: number | null;
  distBase: number | null;
  gatedBase: boolean;
  pendingBase: boolean;
  ourBasePercentile: number | null;
  ourBaseRank: number | null;
}

/** Gates a p50/p90/max distribution triple together; drops a half-measured or inverted (max < p90) band pair without clamping or filling. */
function gatedTriple(
  gateOpen: boolean,
  p50: number | null,
  p90: number | null,
  max: number | null,
): { p50: number | null; p90: number | null; max: number | null } {
  let p90v = gateOpen ? p90 : null;
  let maxv = gateOpen ? max : null;
  if (p90v === null || maxv === null || maxv < p90v) {
    p90v = null;
    maxv = null;
  }
  return { p50: gateOpen ? p50 : null, p90: p90v, max: maxv };
}

function plotRow(epoch: number, j: EpochJoined | undefined): PlotRow {
  const c = j?.cluster;

  // Net (incl. MEV): p50/p90/max gate together — none of them is a network
  // statistic when the distribution covered under half the cluster.
  const cov = distributionCoverage(c);
  const gateOpen = cov !== null && cov.sufficient;
  const net = gatedTriple(
    gateOpen,
    c?.delegator_apy_p50_pct ?? null,
    c?.delegator_apy_p90_pct ?? null,
    c?.delegator_apy_max_pct ?? null,
  );

  // Base (inflation-only): same gate rule, independent coverage basis.
  const covBase = distributionCoverageInfl(c);
  const gateOpenBase = covBase !== null && covBase.sufficient;
  const base = gatedTriple(
    gateOpenBase,
    c?.delegator_apy_infl_p50_pct ?? null,
    c?.delegator_apy_infl_p90_pct ?? null,
    c?.delegator_apy_infl_max_pct ?? null,
  );

  return {
    epoch,
    our: j?.apy?.delegator_apy_pct ?? null,
    median: net.p50,
    p90: net.p90,
    max: net.max,
    dist: gateOpen ? cov.measured : null,
    gated: cov !== null && !cov.sufficient,
    // Plain absence, and NOT the gate: the /cluster row exists but carries no
    // distribution yet — it is computed during the following epoch. A
    // withheld measurement and an unmade one are different facts and must
    // never share a reason ("withheld below 50% coverage" would be false
    // here: coverage is not low, it does not exist). Without this the epoch
    // falls through both branches and the cluster line vanishes unexplained.
    pending: j?.cluster !== undefined && cov === null,
    ourBase: j?.apy?.delegator_apy_inflation_pct ?? null,
    medianBase: base.p50,
    p90Base: base.p90,
    maxBase: base.max,
    distBase: gateOpenBase ? covBase.measured : null,
    gatedBase: covBase !== null && !covBase.sufficient,
    pendingBase: j?.cluster !== undefined && covBase === null,
    ourBasePercentile: c?.our_base_percentile_pct ?? null,
    ourBaseRank: c?.our_base_rank ?? null,
  };
}

function hasPlottableValue(r: PlotRow): boolean {
  return (
    r.our !== null ||
    r.median !== null ||
    r.p90 !== null ||
    r.ourBase !== null ||
    r.medianBase !== null ||
    r.p90Base !== null
  );
}

/**
 * Join the endpoints by epoch and build null-preserving, gated, end-trimmed
 * series arrays.
 *
 * fromEpoch/toEpoch filter client-side (the /apy, /income, and /rank
 * requests are unfiltered). Returns null when no epoch has a plottable
 * value. `rank` joins into byEpoch only (§3.10 vote-credit standing feeds
 * the answer band's rank chip, never a plotted series); it never affects
 * the trimmed spine.
 */
export function assemble(
  apy: readonly ApyRow[],
  cluster: ClusterData,
  income: readonly IncomeRow[],
  fromEpoch?: number,
  toEpoch?: number,
  rank: readonly RankRow[] = [],
): Assembled | null {
  const byEpoch = new Map<number, EpochJoined>();
  const joined = (epoch: number): EpochJoined => {
    let j = byEpoch.get(epoch);
    if (j === undefined) {
      j = {};
      byEpoch.set(epoch, j);
    }
    return j;
  };

  for (const row of apy) {
    if (inRange(row.epoch, fromEpoch, toEpoch)) {
      joined(row.epoch).apy = row;
    }
  }
  for (const row of cluster.rows) {
    if (inRange(row.epoch, fromEpoch, toEpoch)) {
      joined(row.epoch).cluster = row;
    }
  }

  const spineEpochs = [...byEpoch.keys()];
  if (spineEpochs.length === 0) {
    return null;
  }

  // Build the full range first, then trim both ends to the first/last epoch
  // with a plottable value. Interior all-null epochs stay as gaps; epochs
  // outside the trimmed spine do not appear on the axis at all.
  const full: PlotRow[] = [];
  const loFull = Math.min(...spineEpochs);
  const hiFull = Math.max(...spineEpochs);
  for (let epoch = loFull; epoch <= hiFull; epoch += 1) {
    full.push(plotRow(epoch, byEpoch.get(epoch)));
  }
  let firstIdx = -1;
  let lastIdx = -1;
  for (let i = 0; i < full.length; i += 1) {
    const r = full[i];
    if (r !== undefined && hasPlottableValue(r)) {
      if (firstIdx === -1) {
        firstIdx = i;
      }
      lastIdx = i;
    }
  }
  if (firstIdx === -1) {
    return null;
  }
  const rows = full.slice(firstIdx, lastIdx + 1);
  const lo = rows[0]?.epoch ?? loFull;
  const hi = rows[rows.length - 1]?.epoch ?? hiFull;

  const incomeInRange = income
    .filter((row) => row.epoch >= lo && row.epoch <= hi)
    .sort((a, b) => a.epoch - b.epoch);
  for (const row of incomeInRange) {
    joined(row.epoch).income = row;
  }

  const rankInRange = rank.filter((row) => row.epoch >= lo && row.epoch <= hi);
  for (const row of rankInRange) {
    joined(row.epoch).rank = row;
  }

  return {
    epochs: rows.map((r) => r.epoch),
    our: rows.map((r) => r.our),
    median: rows.map((r) => r.median),
    p90: rows.map((r) => r.p90),
    max: rows.map((r) => r.max),
    bandDelta: rows.map((r) => (r.p90 !== null && r.max !== null ? r.max - r.p90 : null)),
    distributionValidators: rows.map((r) => r.dist),
    distributionGated: rows.map((r) => r.gated),
    distributionPending: rows.map((r) => cluster.distributionKeysPresent && r.pending),
    hasMedian: rows.some((r) => r.median !== null),
    hasBand: rows.some((r) => r.p90 !== null),
    distributionKeysPresent: cluster.distributionKeysPresent,
    ourBase: rows.map((r) => r.ourBase),
    medianBase: rows.map((r) => r.medianBase),
    p90Base: rows.map((r) => r.p90Base),
    maxBase: rows.map((r) => r.maxBase),
    bandDeltaBase: rows.map((r) => (r.p90Base !== null && r.maxBase !== null ? r.maxBase - r.p90Base : null)),
    distributionInflValidators: rows.map((r) => r.distBase),
    distributionInflGated: rows.map((r) => r.gatedBase),
    distributionInflPending: rows.map((r) => cluster.inflationKeysPresent && r.pendingBase),
    hasMedianBase: rows.some((r) => r.medianBase !== null),
    hasBandBase: rows.some((r) => r.p90Base !== null),
    inflationKeysPresent: cluster.inflationKeysPresent,
    ourBasePercentile: rows.map((r) => r.ourBasePercentile),
    ourBaseRank: rows.map((r) => r.ourBaseRank),
    byEpoch,
    incomeFirst: incomeInRange.length > 0 ? (incomeInRange[0] ?? null) : null,
    incomeLast: incomeInRange.length > 0 ? (incomeInRange[incomeInRange.length - 1] ?? null) : null,
  };
}

export interface GatedRegion {
  /** Inclusive index range into the trimmed epoch spine (Assembled.epochs). */
  startIndex: number;
  endIndex: number;
  startEpoch: number;
  endEpoch: number;
}

/**
 * Groups consecutive coverage-gated epochs into contiguous regions for the
 * "partial coverage" shaded-band annotation.
 *
 * One isolated gated epoch is its own single-epoch region; adjacent gated
 * epochs merge into one region; a non-gated epoch never appears inside a
 * region. Input arrays must be the same length (Assembled.epochs /
 * Assembled.distributionGated).
 */
export function gatedRegions(
  epochs: readonly number[],
  gated: readonly boolean[],
): GatedRegion[] {
  const regions: GatedRegion[] = [];
  let start = -1;
  for (let i = 0; i < gated.length; i += 1) {
    if (gated[i] === true) {
      if (start === -1) {
        start = i;
      }
      continue;
    }
    if (start !== -1) {
      regions.push(gatedRegion(epochs, start, i - 1));
      start = -1;
    }
  }
  if (start !== -1) {
    regions.push(gatedRegion(epochs, start, gated.length - 1));
  }
  return regions;
}

function gatedRegion(epochs: readonly number[], startIndex: number, endIndex: number): GatedRegion {
  return {
    startIndex,
    endIndex,
    startEpoch: epochs[startIndex] ?? 0,
    endEpoch: epochs[endIndex] ?? 0,
  };
}

export interface CoverageRun {
  startIndex: number;
  endIndex: number;
  length: number;
}

/**
 * Maximal runs of consecutive `true` indices. Used to switch the median/band
 * series between the continuous area (run length >= 3) and the per-epoch
 * whisker + median tick (run length 1-2) — see chart-options.ts band_sparse
 * rendering.
 */
export function coverageRuns(covered: readonly boolean[]): CoverageRun[] {
  const runs: CoverageRun[] = [];
  let start = -1;
  for (let i = 0; i < covered.length; i += 1) {
    if (covered[i] === true) {
      if (start === -1) {
        start = i;
      }
      continue;
    }
    if (start !== -1) {
      runs.push({ startIndex: start, endIndex: i - 1, length: i - start });
      start = -1;
    }
  }
  if (start !== -1) {
    runs.push({ startIndex: start, endIndex: covered.length - 1, length: covered.length - start });
  }
  return runs;
}

/** Band area (vs. per-epoch whisker) threshold: maximal runs shorter than this render as whiskers. */
export const BAND_RUN_MIN_LENGTH = 3;

export interface HeadlineEpoch {
  index: number;
  epoch: number;
  ourPct: number;
  p50: number;
  p90: number;
  max: number;
  /**
   * True when the true latest fully-covered epoch's /apy value was null and
   * an earlier fully-covered, non-null epoch was used instead — the caller
   * must say so in the headline label.
   */
  usedFallback: boolean;
}

/** One basis's parallel series, read out of Assembled by selectHeadlineEpoch/selectHeadlineEpochBase. */
interface BasisSeries {
  epochs: readonly number[];
  our: readonly (number | null)[];
  median: readonly (number | null)[];
  p90: readonly (number | null)[];
  max: readonly (number | null)[];
  gated: readonly boolean[];
}

function netBasis(a: Assembled): BasisSeries {
  return { epochs: a.epochs, our: a.our, median: a.median, p90: a.p90, max: a.max, gated: a.distributionGated };
}

function baseBasis(a: Assembled): BasisSeries {
  return {
    epochs: a.epochs,
    our: a.ourBase,
    median: a.medianBase,
    p90: a.p90Base,
    max: a.maxBase,
    gated: a.distributionInflGated,
  };
}

function toHeadlineEpoch(s: BasisSeries, index: number, usedFallback: boolean): HeadlineEpoch {
  return {
    index,
    epoch: s.epochs[index] ?? 0,
    ourPct: s.our[index] ?? 0,
    p50: s.median[index] ?? 0,
    p90: s.p90[index] ?? 0,
    max: s.max[index] ?? 0,
    usedFallback,
  };
}

/**
 * Selects the headline/strip source epoch for one basis's series: the
 * latest epoch that is BOTH fully covered (cluster distribution not gated,
 * median/p90/max all present) AND non-null (our value present). Never a
 * gated or null epoch, per the honesty invariant — selected by rule, never
 * hardcoded.
 *
 * If the true latest fully-covered epoch's our value is null, falls back to
 * the most recent fully-covered epoch that also has a non-null our value,
 * flagging `usedFallback` so the caller can disclose the substitution.
 * Returns null when the window has zero fully covered epochs.
 */
function selectHeadlineFrom(s: BasisSeries): HeadlineEpoch | null {
  let latestCovered = -1;
  for (let i = s.epochs.length - 1; i >= 0; i -= 1) {
    if (s.gated[i] !== true && s.median[i] !== null && s.p90[i] !== null && s.max[i] !== null) {
      latestCovered = i;
      break;
    }
  }
  if (latestCovered === -1) {
    return null;
  }
  if (s.our[latestCovered] !== null) {
    return toHeadlineEpoch(s, latestCovered, false);
  }
  for (let i = latestCovered - 1; i >= 0; i -= 1) {
    if (s.gated[i] !== true && s.median[i] !== null && s.p90[i] !== null && s.max[i] !== null && s.our[i] !== null) {
      return toHeadlineEpoch(s, i, true);
    }
  }
  return null;
}

/** selectHeadlineFrom over the NET (incl. MEV) series. */
export function selectHeadlineEpoch(a: Assembled): HeadlineEpoch | null {
  return selectHeadlineFrom(netBasis(a));
}

/** selectHeadlineFrom over the BASE (inflation-only) series. */
export function selectHeadlineEpochBase(a: Assembled): HeadlineEpoch | null {
  return selectHeadlineFrom(baseBasis(a));
}

function hasCoveredEpochIn(s: BasisSeries): boolean {
  for (let i = 0; i < s.epochs.length; i += 1) {
    if (s.gated[i] !== true && s.median[i] !== null && s.p90[i] !== null && s.max[i] !== null) {
      return true;
    }
  }
  return false;
}

/** True when at least one epoch is fully cluster-covered on the NET series (gate open, p50/p90/max all present), independent of our /apy. */
export function hasCoveredEpoch(a: Assembled): boolean {
  return hasCoveredEpochIn(netBasis(a));
}

/** True when at least one epoch is fully cluster-covered on the BASE series, independent of our /apy. */
export function hasCoveredEpochBase(a: Assembled): boolean {
  return hasCoveredEpochIn(baseBasis(a));
}

/**
 * Client-side window control: the last `epochCount` epochs of an already
 * -assembled series (no refetch). `null` means the full history. Options
 * beyond the available history are the caller's responsibility to disable;
 * this clamps defensively to the full range rather than padding with
 * synthetic epochs.
 */
export function windowSlice(a: Assembled, epochCount: number | null): Assembled {
  if (epochCount === null || epochCount >= a.epochs.length) {
    return a;
  }
  const start = a.epochs.length - epochCount;
  const slice = <T,>(arr: readonly T[]): T[] => arr.slice(start);
  const epochs = slice(a.epochs);
  const median = slice(a.median);
  const p90 = slice(a.p90);
  const medianBase = slice(a.medianBase);
  const p90Base = slice(a.p90Base);
  const firstEpoch = epochs[0];
  return {
    epochs,
    our: slice(a.our),
    median,
    p90,
    max: slice(a.max),
    bandDelta: slice(a.bandDelta),
    distributionValidators: slice(a.distributionValidators),
    distributionGated: slice(a.distributionGated),
    distributionPending: slice(a.distributionPending),
    hasMedian: median.some((v) => v !== null),
    hasBand: p90.some((v) => v !== null),
    distributionKeysPresent: a.distributionKeysPresent,
    ourBase: slice(a.ourBase),
    medianBase,
    p90Base,
    maxBase: slice(a.maxBase),
    bandDeltaBase: slice(a.bandDeltaBase),
    distributionInflValidators: slice(a.distributionInflValidators),
    distributionInflGated: slice(a.distributionInflGated),
    distributionInflPending: slice(a.distributionInflPending),
    hasMedianBase: medianBase.some((v) => v !== null),
    hasBandBase: p90Base.some((v) => v !== null),
    inflationKeysPresent: a.inflationKeysPresent,
    ourBasePercentile: slice(a.ourBasePercentile),
    ourBaseRank: slice(a.ourBaseRank),
    byEpoch: a.byEpoch,
    incomeFirst: firstEpoch !== undefined ? (a.byEpoch.get(firstEpoch)?.income ?? null) : null,
    incomeLast: a.incomeLast,
  };
}
